import React, { useRef, useState } from "react";
import { RefreshCw, Upload, Loader2, Wand2, X, Square, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PhotoCropper, checkPhotoFile, loadPhotoDownscaled } from "./PhotoCropper";
import { PortraitEngineCards, PortraitPresetChips, ReferencePhotoRules, usePortraitEngines } from "./PortraitEngine";

// Portrait actions for an assistant: upload your own photo (cropped, moderated, free) or (re)generate with AI — Standard or Cinematic engine.
export function PortraitTools({ persona, onChange, busy }) {
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFor, setCropFor] = useState("portrait");  // "portrait" = use photo as portrait · "reference" = reference for the AI run
  const [form, setForm] = useState(false);
  const [extra, setExtra] = useState("");
  const [style, setStyle] = useState(persona.profile?.appearance?.visual_style || "cinematic realistic");
  const [useRef_, setUseRef] = useState(!!persona.reference_photo);
  const [newRef, setNewRef] = useState(null);
  const [engine, setEngine] = useState("standard");
  const [preset, setPreset] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const fileRef = useRef(null);
  const engines = usePortraitEngines();
  const price = engines?.find((e) => e.id === engine)?.credits;
  const disabled = busy || uploading || starting;

  const pick = (kind) => { setCropFor(kind); fileRef.current?.click(); };
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const err = checkPhotoFile(f);
    if (err) { toast.error(err); return; }
    try { setCropSrc(await loadPhotoDownscaled(f)); }  // downscaled in the browser, then crop/zoom, then moderate + save
    catch { toast.error("Foto tidak bisa dibaca. Coba JPG atau PNG."); }
  };
  const applyCrop = async (dataUrl) => {
    setCropSrc(null);
    if (cropFor === "reference") { setNewRef(dataUrl); setUseRef(true); return; }
    setUploading(true);
    try {
      const r = await api.post(`/personas/${persona.id}/portrait/upload`, { photo: dataUrl });
      onChange(r.data); toast.success("Foto dipakai sebagai potret");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengunggah foto"); } finally { setUploading(false); }
  };

  const regen = async () => {
    setStarting(true);
    try {
      const r = await api.post(`/personas/${persona.id}/portrait`, { style: style.trim() || "cinematic realistic", extra: extra.trim() || null, use_reference: useRef_, engine, preset: engine === "cinematic" ? preset : null, reference_photo: newRef });
      onChange({ ...persona, reference_photo: newRef || persona.reference_photo, portrait_job: { status: "running", engine, credits: r.data?.credits } }); setForm(false); setExtra(""); setNewRef(null);
      toast.success(engine === "cinematic" ? `Potret Sinematik dimulai — ${r.data?.credits} kredit ditahan, dikembalikan bila gagal` : "Potret sedang dibuat…");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal memulai pembuatan potret"); } finally { setStarting(false); }
  };
  const stop = async () => {
    setStopping(true);
    try {
      const r = await api.post(`/personas/${persona.id}/portrait/cancel`);
      onChange({ ...persona, portrait_job: { status: "error", error: r.data?.refunded ? `Dihentikan — ${r.data.refunded} kredit dikembalikan` : "Dihentikan" } });
      toast.success(r.data?.refunded ? `Dihentikan, ${r.data.refunded} kredit dikembalikan` : "Pembuatan potret dihentikan");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghentikan"); } finally { setStopping(false); }
  };

  const btn = "flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 py-2.5 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-60";
  const refOn = useRef_ && (newRef || persona.reference_photo);
  return (
    <div className="mt-3 space-y-2" data-testid="portrait-tools">
      {cropSrc && <PhotoCropper src={cropSrc} onDone={applyCrop} onCancel={() => setCropSrc(null)} title={cropFor === "reference" ? "Atur foto referensi" : undefined} />}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="portrait-file-input" />
      <button onClick={() => pick("portrait")} disabled={disabled} className={btn} data-testid="persona-upload-portrait">
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Memeriksa & mengunggah…" : "Unggah Foto Sendiri"}
      </button>
      {busy ? (
        <button onClick={stop} disabled={stopping} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#EF4444]/40 bg-[#FEF2F2] py-2.5 text-sm font-semibold text-[#B91C1C] hover:bg-[#FEE2E2] disabled:opacity-60" data-testid="portrait-cancel">
          {stopping ? <Loader2 size={15} className="animate-spin" /> : <Square size={13} className="fill-current" />} Hentikan pembuatan potret{persona.portrait_job?.engine === "cinematic" ? " (kredit dikembalikan)" : ""}
        </button>
      ) : (
        <button onClick={() => setForm((v) => !v)} disabled={disabled} className={btn} data-testid="persona-regen-portrait">
          <RefreshCw size={15} /> Buat Ulang Potret dengan AI
        </button>
      )}
      {form && !busy && (
        <div className="space-y-2 rounded-2xl border border-[#D9E2F2] bg-[#F5F8FF] p-3" data-testid="portrait-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-700">Potret baru</p>
            <button onClick={() => setForm(false)} className="text-slate-400 hover:text-slate-600" aria-label="Tutup"><X size={14} /></button>
          </div>
          <PortraitEngineCards engines={engines} value={engine} onChange={setEngine} disabled={disabled} />
          {engine === "cinematic" && <PortraitPresetChips engines={engines} engine={engine} value={preset} onChange={setPreset} disabled={disabled} />}
          <textarea className="input-dark min-h-[90px] text-sm" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="portrait-extra"
            placeholder="Contoh: tersenyum ramah, latar kantor modern, kemeja biru muda, pencahayaan hangat…" />
          <input className="input-dark py-2 text-sm" value={style} onChange={(e) => setStyle(e.target.value)} placeholder="Gaya visual (mis. cinematic realistic, ilustrasi anime)" data-testid="portrait-style" />
          <div className="flex flex-wrap items-center gap-2">
            {(persona.reference_photo || newRef) && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={useRef_} onChange={(e) => setUseRef(e.target.checked)} data-testid="portrait-use-ref" /> Gunakan foto referensi
              </label>
            )}
            <button type="button" onClick={() => pick("reference")} disabled={disabled} className="flex items-center gap-1.5 rounded-lg border border-[#C9D6F2] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#2F6BFF] hover:bg-[#EEF3FF] disabled:opacity-60" data-testid="portrait-pick-reference">
              <ImagePlus size={13} /> {newRef ? "Ganti foto referensi" : persona.reference_photo ? "Foto referensi baru" : "Dari foto referensi"}
            </button>
            {newRef && <img src={newRef} alt="" className="h-9 w-9 rounded-lg object-cover ring-1 ring-[#C9D6F2]" data-testid="portrait-new-ref-thumb" />}
          </div>
          {refOn && <ReferencePhotoRules compact />}
          <p className="text-[11px] text-slate-500" data-testid="portrait-price-note">
            {price != null ? <>Biaya <b>{price} kredit</b> per potret. </> : null}
            {engine === "cinematic" ? "Kredit ditahan saat mulai, dilunasi saat potret tersimpan, dan dikembalikan penuh bila gagal atau dihentikan." : "Kredit hanya dipotong bila potret berhasil dibuat; potret yang gagal tidak dikenakan biaya."}
          </p>
          <button onClick={regen} disabled={disabled} className="btn-grad flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm disabled:opacity-60" data-testid="portrait-generate">
            {starting ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Buat Potret{price != null ? ` · ${price} kredit` : ""}
          </button>
        </div>
      )}
    </div>
  );
}
